/* eslint-disable @typescript-eslint/camelcase */
import React, { useState, ReactElement } from 'react';
import { History } from 'history';
import { debounce } from 'lodash';
import { Lesson } from '@fenderdigital/api-definitions/play';
import RepositionElement from '@fenderdigital/react-ui-kit/reposition-element';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import VideoEndscreen from '../../../components/VideoEndscreen';
import VideoPlayer from '../../../components/VideoPlayer';
import VideoRepositionControls from '../../../components/VideoRepositionControls';
import { getVideoPlayerPropsDeprecated, getEndscreenProps } from '../../../utils/curriculum-utils';
import { videoPlaybackEvents } from '../../../components/VideoPlayer/VideoPlayerConstants';
import {
  EventNames,
  getAlgoliaTracking,
  getPracticeTrackEvent,
  trackEventAndUpdate,
} from '../../../lib/analytics';
import segment from '../../../core/segment';
import { getVideoSpeedTrackData } from '../../../components/VideoPlayer/VideoPlayerAnalytics';
import { preventRightClick } from './lesson-utils';
import useQueueStreaksModal from '../../../hooks/useQueueStreaksModal';

interface LessonVideoProps {
  autoplay: boolean;
  history: History;
  canReposition?: boolean;
  className?: string;
  fullScreenStyles?: { [css: string]: string };
  isTabFullScreen?: boolean;
  isRepositioned: boolean;
  lesson?: Lesson;
  nextLesson?: Lesson;
  onVideoReposition: () => void;
  onWatchedVideoProgress: (percentage: number, seconds: number) => void;
  trackData?: any;
  updateLessonProgress: (
    lessonID: string,
    lessonType: string,
    videoWatchedSeconds: number,
    instrument: string,
    videoTotalSeconds: number,
  ) => void;
  updateStreakVideos: () => void;
  shouldQueueStreaksModal: (queueModal: boolean) => void;
  startAtTime?: number;
  videoID: string;
  nextURL: string;
  sessionId: string | undefined;
  trackingActivityProperties: {};
  curriculumProperties: {};
  userActivitiesCompleted: {};
  currentCourse: {
    id: string;
    slug: string;
    title: string;
    type: string;
  };
  courseCompleted: boolean;
  userID: string;
}

type Player = {
  video: {
    duration: number;
    time: number;
  };
};

const { event } = segment;

const LessonVideo: React.FC<LessonVideoProps> = props => {
  const {
    nextURL,
    lesson,
    history,
    isRepositioned,
    startAtTime,
    trackData = {},
    isTabFullScreen = false,
    fullScreenStyles = {},
    onVideoReposition = (): void => {},
    onWatchedVideoProgress = (): void => {},
    updateLessonProgress,
    updateStreakVideos = (): void => {},
    shouldQueueStreaksModal,
    autoplay,
    trackingActivityProperties,
    curriculumProperties,
    userActivitiesCompleted,
    currentCourse,
    courseCompleted,
    userID,
  } = props;
  const [videoComplete, setVideoComplete] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoStreaksWatched, setVideoStreaksWatched] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [activityRepeated, setActivityRepeated] = useState(0);
  const { isFreeUser } = usePlayUser();
  const queueStreaksModal = useQueueStreaksModal();

  const onContinue = (nextVideoType: string): void => {
    if (nextVideoType === 'practice-sheet') {
      const { eventType, properties } = getPracticeTrackEvent(
        'Practice Mode Launched',
        lesson,
        window.location.href,
      );
      event(eventType, properties);
    }
    history.push(nextURL);
    setVideoComplete(false);
  };

  const onVideoEnd = (): void => {
    const { id = '', instrument = '', type = '' } = lesson || {};
    const lessonVideo = lesson?.['activity-lesson-video'];
    const { duration } = lessonVideo || {};
    const endTime = Math.round(duration || 1);
    if (courseCompleted) {
      trackEventAndUpdate(
        userID,
        EventNames.COURSE_COMPLETED,
        {
          ...trackingActivityProperties,
          course_id: currentCourse.id,
          course_slug: currentCourse.slug,
          course_name: currentCourse.title,
          ...getAlgoliaTracking('conversion', currentCourse.id),
        },
        userActivitiesCompleted,
      );
    }
    updateLessonProgress(id, type, endTime, instrument, endTime);
    setVideoComplete(true);
    setVideoStreaksWatched(false);
    queueStreaksModal(false);
  };

  const onVideoError = (): void => {
    setVideoError(true);
  };

  const onVideoProgress = (player: Player): void => {
    if (!player) return;
    const { id = '', instrument = '', type = '' } = lesson || {};
    const { video } = player;
    const { duration, time } = video;
    const videoWatchedSeconds = Math.round(time);
    const videoTotalSeconds = Math.round(duration);
    updateLessonProgress(id, type, videoWatchedSeconds, instrument, videoTotalSeconds);
    if (!videoStarted) {
      setVideoStarted(true);
      queueStreaksModal(true);
      trackEventAndUpdate(
        userID,
        EventNames.ACTIVITY_STARTED,
        {
          ...trackingActivityProperties,
          repeat: activityRepeated,
          ...getAlgoliaTracking('click', id),
        },
        curriculumProperties,
      );
    }

    const percentage = Math.round((time / duration) * 100);
    if (percentage > 75 && !videoStreaksWatched) {
      updateStreakVideos();
      setVideoStreaksWatched(true);
    }
  };

  const onVideoEvent = (videoEvent: string, player: Player): void | boolean | null => {
    switch (videoEvent) {
      case videoPlaybackEvents.FINISH:
        return onVideoEnd();
      case videoPlaybackEvents.ERROR:
        return onVideoError();
      case videoPlaybackEvents.TEN_SECONDS:
        return onVideoProgress(player);
      case videoPlaybackEvents.PAUSE:
        return queueStreaksModal(false);
      // explicitly called out so future devs know all events
      case videoPlaybackEvents.BUFFER:
      case videoPlaybackEvents.CUEPOINT:
      case videoPlaybackEvents.HLS_ERROR:
      case videoPlaybackEvents.HLS_LEVEL_SWITCH:
      case videoPlaybackEvents.PROGRESS:
      case videoPlaybackEvents.READY:
      case videoPlaybackEvents.RESUME:
      case videoPlaybackEvents.RESUME_SEEK:
      case videoPlaybackEvents.SEEK:
      case videoPlaybackEvents.WATCHING:
      default:
        return false;
    }
  };

  const onLessonVideoProgress = (pcnt: number, seconds: number): void => {
    onWatchedVideoProgress(pcnt, seconds);
  };

  const renderVideoEndscreen = (): ReactElement => {
    const endscreenProps = getEndscreenProps(lesson, props);
    const { nextVideoType } = endscreenProps || '';
    const nextLessonClick = (): void => onContinue(nextVideoType);
    return (
      <VideoEndscreen
        {...endscreenProps}
        fullScreenStyles={fullScreenStyles}
        onContinue={nextLessonClick}
        onRestart={(): void => {
          setVideoComplete(false);
          setVideoStarted(false);
          setActivityRepeated(activityRepeated + 1);
        }}
      />
    );
  };

  const renderVideoPlayer = (): ReactElement => {
    const { fc_id, video_id } = trackData || {};
    const videoProps = getVideoPlayerPropsDeprecated({ lesson, startAtTime });
    if (videoError) {
      // TODO remove ignore once VideoPlayer is in TS
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      return VideoPlayer.renderBasicLessonVideo(lesson, null, onVideoEnd, isTabFullScreen);
    }
    const speedTrackData = getVideoSpeedTrackData({ fc_id, ...lesson });
    return (
      <VideoPlayer
        {...videoProps}
        fullScreenStyles={fullScreenStyles}
        onEvent={debounce(onVideoEvent, 400)}
        onWatchedVideoProgress={onLessonVideoProgress}
        onIsUserBusy={shouldQueueStreaksModal}
        renderRepositionControls={(player: Player): ReactElement => (
          <VideoRepositionControls
            fcId={fc_id}
            isRepositioned={isRepositioned}
            onClick={onVideoReposition}
            player={player}
            videoId={video_id}
          />
        )}
        trackData={trackData}
        speedTrackData={speedTrackData}
        shouldPause={isTabFullScreen}
        autoplay={autoplay}
      />
    );
  };

  const smallVideoHeight = (window.innerWidth * 9) / 16;
  return (
    <div
      onContextMenu={preventRightClick}
      className={`lesson-video ${
        isRepositioned ? 'is-repositioned' : ''
      } relative center bg-black h-100`}
    >
      <RepositionElement
        dimensions={{
          width: 320,
          height: 180,
        }}
        isActive={isRepositioned}
        position={{
          top: isFreeUser ? 110 : 60,
          right: 0,
        }}
      >
        <div style={{ minHeight: `${smallVideoHeight}` }}>
          {videoComplete ? renderVideoEndscreen() : renderVideoPlayer()}
        </div>
      </RepositionElement>
    </div>
  );
};

export default LessonVideo;
